import { convertToBoolean, isFeatureEnabled } from 'src/utils'
import { isSepaPaymentType } from './paymentNameUtils'
import { APPConfig } from 'config/appConfig'
import { checkoutContainer } from 'src/models'

/**
 * Returns true if Paymetric is enabled via feature flag
 */
export const isPaymetricEnabled = () => {
  return convertToBoolean(
    APPConfig?.getAppConfig()?.enableDirectPaymetricAndAccertifyIntegration
  )
}

/**
 * Normalizes Paymetric card type for payload contracts.
 * When AMEX payments are enabled, convert AMEX -> AMERICANEXPRESS.
 */
export const normalizePaymetricCardType = cardType => {
  const normalizedType = (cardType || '').toString().toUpperCase()
  const isPaymetricFlowEnabled = convertToBoolean(
    APPConfig?.getAppConfig()?.enableDirectPaymetricAndAccertifyIntegration
  )

  if (isPaymetricFlowEnabled && normalizedType === 'AMEX') {
    return 'AMERICANEXPRESS'
  }

  return normalizedType
}

/**
 * Returns the SEPA iconURL from the GPS payment options response, matching the
 * icon shown when adding a new SEPA payment, so saved/subscription views can reuse it.
 */
export const getSepaIconURL = () => {
  const gpsPaymentOptions = checkoutContainer.globalPaymentResponse || {}
  // The SEPA key is a content-managed label, so match the option id as well.
  const sepaKeys = Object.keys(gpsPaymentOptions).filter(
    key =>
      isSepaPaymentType(key) ||
      (gpsPaymentOptions[key] || []).some(option =>
        isSepaPaymentType(option?.id)
      )
  )
  const sepaKey =
    sepaKeys.find(key => gpsPaymentOptions[key]?.length) || sepaKeys[0]
  const sepaPaymentInfo = (sepaKey && gpsPaymentOptions[sepaKey]) || []
  return (sepaPaymentInfo?.[0]?.iconURL || '').trim()
}

/**
 * Normalizes provider field for Paymetric payloads.
 * Keep cardType untouched; only map provider AMEX -> AMERICANEXPRESS
 * when the Paymetric AMEX flag is enabled.
 */
export const normalizePaymetricProvider = provider => {
  return normalizePaymetricCardType(provider)
}

/**
 * Returns the active payment form data from the correct container
 * (Paymetric or TokenEx based on feature flag)
 */
export const getActivePaymentFormData = ({
  tokenExContainer,
  paymetricContainer,
} = {}) => {
  if (isPaymetricEnabled()) {
    return paymetricContainer?.paymetricFormData || {}
  }
  return tokenExContainer?.tokenExFormData || {}
}

/**
 * Returns the active payment validation data from the correct container
 */
export const getActivePaymentValidData = ({
  tokenExContainer,
  paymetricContainer,
} = {}) => {
  if (isPaymetricEnabled()) {
    return paymetricContainer?.paymetricValidData || {}
  }
  return tokenExContainer?.tokenExValidData || {}
}

/**
 * Determines whether a saved payment was itself stored as a GPS payment, based on its
 * own type/name rather than market-level GPS configuration. Mirrors isGPSPaymentUpdate
 * in src/models/Customer/Customer.js. Use with getNormalizedWalletProfileProperties's
 * isGPSOptionResolved flag when editing a specific saved card.
 * @param {Object} savedPayment - the saved payment being edited
 * @returns {Object|undefined} an option-like object with a name, or undefined
 */
export const getSavedCardGPSOption = savedPayment => {
  const paymentType = (savedPayment?.type || '').toString().toUpperCase()
  const paymentName = (savedPayment?.name || '').toString().trim().toLowerCase()
  const configuredProcessorName = (
    APPConfig.getAppConfig()?.gpsSavedCardProcessorName || ''
  )
    .toString()
    .trim()
    .toLowerCase()

  const isGPSSavedCard =
    !paymentName.includes('paymetric') &&
    (paymentType === 'GPS' ||
      (configuredProcessorName && paymentName === configuredProcessorName))

  return isGPSSavedCard ? { name: savedPayment?.name } : undefined
}

/**
 * Resolves the processor option deciding the token field/paymentOptionType for a
 * wallet/profile payload. When isGPSOptionResolved is true, trusts gpsPaymentOption
 * as-is (skipping the checkoutContainer market-level fallback) instead of falling
 * back to checkoutContainer's market-level GPS state.
 * Callers only ever read this via optional chaining, so an unresolved option is
 * left as undefined rather than padded with an empty object.
 */
const resolveGPSProcessorOption = (gpsPaymentOption, isGPSOptionResolved) => {
  if (isGPSOptionResolved) {
    return gpsPaymentOption
  }
  return (
    gpsPaymentOption ?? checkoutContainer?.getValidGPSCreditCardProcessor?.()
  )
}

/**
 * Normalizes payment properties for wallet/profile add-payment payloads.
 * In Paymetric flow, maps token fields to paymetricToken keys.
 * @param {Object} [gpsPaymentOption] - Processor handling this payment; decides the token field.
 * @param {boolean} [isGPSOptionResolved] - Skip the checkoutContainer market-level fallback and
 * trust gpsPaymentOption as-is. Use when the caller already knows, per-card, whether it's GPS.
 */
export const getNormalizedWalletProfileProperties = ({
  paymentContainer,
  paymetricContainer,
  gpsPaymentOption,
  isGPSOptionResolved = false,
} = {}) => {
  const isPaymetricFlow = paymentContainer === paymetricContainer
  // Token field follows the processor handling this payment, not any Paymetric option in the response.
  const resolvedProcessorOption = resolveGPSProcessorOption(
    gpsPaymentOption,
    isGPSOptionResolved
  )
  const hasGPSPaymentOption = Boolean(
    resolvedProcessorOption?.name || resolvedProcessorOption?.processorURL
  )

  if (!isPaymetricFlow) {
    const tokenExProperties = { ...(paymentContainer?.tokenExFormData || {}) }
    if (hasGPSPaymentOption && resolvedProcessorOption?.type) {
      tokenExProperties.paymentOptionType = resolvedProcessorOption.type
    }
    return tokenExProperties
  }
  // Simplify Paymetric flow: cvv, cvvIncluded, firstSix, lastFour, paymetricToken
  const pmData = paymentContainer?.paymetricFormData || {}
  let cvvIncluded
  if (typeof pmData.cvvIncluded === 'boolean') {
    cvvIncluded = pmData.cvvIncluded
  } else if (typeof pmData.includeCvv === 'boolean') {
    cvvIncluded = pmData.includeCvv
  }
  const isPaymetric3DS = (resolvedProcessorOption?.name || '')
    .toLowerCase()
    .includes('paymetric')
  const shouldUsePaymetricToken = isPaymetric3DS || !hasGPSPaymentOption
  const tokenProperty = shouldUsePaymetricToken
    ? { paymetricToken: pmData.token }
    : { token: pmData.token }
  const properties = {
    ...(typeof cvvIncluded === 'boolean' && { cvvIncluded }),
    ...(cvvIncluded && pmData.cvv && { cvv: pmData.cvv }),
    ...(pmData.cardType && {
      cardType: normalizePaymetricCardType(pmData.cardType),
    }),
    ...(pmData.firstSix && { firstSix: pmData.firstSix }),
    ...(pmData.lastFour && { lastFour: pmData.lastFour }),
    ...(pmData.token && tokenProperty),
  }

  const detectedCardType = paymetricContainer?.getCardProviderFromTokenData(
    paymentContainer?.paymetricFormData || {}
  )

  if (detectedCardType && !properties.cardType) {
    properties.cardType = normalizePaymetricCardType(detectedCardType)
  }

  if (hasGPSPaymentOption && resolvedProcessorOption?.type) {
    properties.paymentOptionType = resolvedProcessorOption.type
  }

  return properties
}

/**
 * Returns normalized paymetric/tokenEx properties and provider
 * for subscription payment payloads.
 * @param {boolean} [isGPSOptionResolved] - Trust gpsPaymentOption as-is so the token field
 * matches the payload's declared payment type instead of market-level GPS state.
 */
export const getSubscriptionPaymentPayloadData = ({
  paymentContainer,
  paymetricContainer,
  provider = '',
  gpsPaymentOption,
  isGPSOptionResolved = false,
} = {}) => {
  const properties = getNormalizedWalletProfileProperties({
    paymentContainer,
    paymetricContainer,
    gpsPaymentOption,
    isGPSOptionResolved,
  })

  const normalizedProvider = isPaymetricEnabled()
    ? normalizePaymetricProvider(properties?.cardType || provider)
    : provider

  return {
    properties,
    provider: normalizedProvider,
  }
}
/**
 * Triggers Paymetric iframe validation on submit:
 * sets paymetricCardTouched and calls $XIFrame.validate()
 */
export const triggerPaymetricValidationOnSubmit = ({
  paymetricContainer,
} = {}) => {
  if (!paymetricContainer) return
  paymetricContainer.paymetricCardTouched = true
  paymetricContainer.paymetricCvvTouched = true
  if (typeof paymetricContainer.validatePaymetricCvv === 'function') {
    paymetricContainer.validatePaymetricCvv()
  }
  if (typeof paymetricContainer.validatePaymetricCard === 'function') {
    paymetricContainer.validatePaymetricCard({ markTouched: true })
  }
}

const RESTRICTED_CARD_PROVIDER = [
  {
    providers: ['AMEX', 'AMERICANEXPRESS'],
    flag: 'enableAmexPayment',
    message: {
      key: 'payment.amexNotAccepted',
      fallback: 'American Express cards are not accepted.',
    },
  },
  {
    providers: ['DINERS'],
    flag: 'enableDinersPayment',
    message: {
      key: 'payment.dinersNotAccepted',
      fallback: 'Diners Club cards are not accepted.',
    },
  },
  {
    providers: ['DISCOVER'],
    flag: 'enableDiscoverPayment',
    message: {
      key: 'payment.discoverNotAccepted',
      fallback: 'Discover cards are not accepted.',
    },
  },
]

const normalizeProvider = cardProvider => {
  return (cardProvider || '').toString().trim().toUpperCase()
}

const getCardProvider = cardProvider => {
  const provider = normalizeProvider(cardProvider)
  return RESTRICTED_CARD_PROVIDER.find(rule =>
    rule.providers.includes(provider)
  )
}

/**
 * Checks if a card provider is restricted, either by feature flag or by
 * market rule (Discover is never accepted in Canada, regardless of flag).
 * - AMEX/AMERICANEXPRESS: controlled by the enableAmexPayment feature flag
 * - DINERS: controlled by the enableDinersPayment feature flag
 * - DISCOVER: controlled by the enableDiscoverPayment feature flag, and
 *   always restricted in Canada
 *
 * @param {string} cardProvider - The normalized card provider (e.g. 'AMEX', 'DINERS', 'VISA')
 * @returns {boolean} true if the card should be blocked
 */
export const isCardProviderRestricted = cardProvider => {
  const rule = getCardProvider(cardProvider)
  if (rule && !isFeatureEnabled(rule.flag)) {
    return true
  }
  return Boolean(checkoutContainer?.isDiscoverInCanada?.(cardProvider))
}

/**
 * Returns the i18n key and fallback text for a card-not-accepted toast message.
 *
 * @param {string} cardProvider - The normalized card provider (e.g. 'AMEX', 'DINERS')
 * @returns {{ key: string, fallback: string } | null}
 */
export const getCardNotAcceptedMessage = cardProvider => {
  return getCardProvider(cardProvider)?.message || null
}

/**
 * Returns the i18n key and fallback text when a card provider is blocked by feature flags.
 *
 * @param {string} cardProvider - The normalized card provider (e.g. 'AMEX', 'DINERS')
 * @returns {{ key: string, fallback: string } | null}
 */
export const getRestrictedCardType = cardProvider => {
  return isCardProviderRestricted(cardProvider)
    ? getCardNotAcceptedMessage(cardProvider)
    : null
}
